import React, { useEffect, useRef } from 'react';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';

const pad = (n) => String(n).padStart(2, '0');
const toIsoDate = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

// Single input, range-select calendar. `from`/`to` are 'YYYY-MM-DD' strings (or '').
const DateRangePicker = ({ from, to, onChange, disabled, className, style, placeholder = 'Select date range…' }) => {
  const inputRef = useRef(null);
  const fpRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    fpRef.current = flatpickr(inputRef.current, {
      mode: 'range',
      dateFormat: 'Y-m-d',
      altInput: true,
      altInputClass: className || '',
      altFormat: 'M j, Y',
      allowInput: false,
      onChange: (selectedDates) => {
        if (selectedDates.length === 2) {
          onChangeRef.current?.(toIsoDate(selectedDates[0]), toIsoDate(selectedDates[1]));
        } else if (selectedDates.length === 0) {
          onChangeRef.current?.('', '');
        }
      },
    });
    // flatpickr's altInput is a raw DOM node it creates itself, so inline
    // `style` passed as a React prop never reaches it - apply it manually.
    // (unlike React, raw DOM .style assignment doesn't auto-append 'px'.)
    if (style && fpRef.current.altInput) {
      Object.entries(style).forEach(([key, value]) => {
        fpRef.current.altInput.style[key] = typeof value === 'number' ? `${value}px` : value;
      });
    }
    return () => fpRef.current?.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!fpRef.current) return;
    if (from && to) {
      fpRef.current.setDate([from, to], false);
    } else if (!from && !to) {
      fpRef.current.clear();
    }
  }, [from, to]);

  useEffect(() => {
    fpRef.current?.set('clickOpens', !disabled);
  }, [disabled]);

  return (
    // Real input flatpickr binds to; kept out of the visible layout via an
    // inline style (wins any specificity tie with .form-control's `display:
    // block`, unlike flatpickr's own `type="hidden"`, which loses that tie).
    // The visible, interactive control is the altInput flatpickr creates
    // next to this one, styled via altInputClass/style above.
    <input
      ref={inputRef}
      placeholder={placeholder}
      disabled={disabled}
      readOnly
      style={{ display: 'none' }}
    />
  );
};

export default DateRangePicker;
